// Frontend mirror of functions/propertyIngestionV2/batchedFirestore.js
// normalizeChannel + a human-readable label map. Legacy leads written before
// the channel taxonomy landed may still carry raw values like
// 'x_spencer_public' or 'mls_buy_box' in data.source — collapse them on read.

const CHANNEL_LABELS = {
    email: 'Email',
    sms: 'SMS',
    x: 'X',
    mls: 'MLS',
    api: 'API',
    manual: 'Manual',
    mcp_claude:  'Claude',
    mcp_chatgpt: 'ChatGPT',
    mcp_gemini:  'Gemini',
    mcp:         'AI Agent'
};

export const normalizeLeadChannel = (raw) => {
    if (!raw) return null;
    const s = String(raw).toLowerCase();
    if (s.startsWith('x_') || s === 'x') return 'x';
    if (s.startsWith('mls') || s === 'mls_buy_box') return 'mls';
    if (s === 'unipile_email' || s === 'email') return 'email';
    if (s === 'sms') return 'sms';
    if (s === 'manual') return 'manual';
    if (s === 'api') return 'api';
    if (s.startsWith('mcp')) return s;
    return null;
};

export const getLeadSourceLabel = (rawSource) => {
    const channel = normalizeLeadChannel(rawSource);
    if (!channel) return 'Unknown';
    return CHANNEL_LABELS[channel] || 'Unknown';
};

// Badge background utility class per channel. Uses approved Variables.css
// classes only (no inline styles, no new colours).
const CHANNEL_BG = {
    email:       'bg-colour-blue-01',
    sms:         'bg-colour-aqua-01',
    x:           'bg-colour-gray-02',
    mls:         'bg-colour-jade-01',
    api:         'bg-colour-purple-01',
    manual:      'bg-colour-orange-01',
    mcp_claude:  'bg-colour-purple-01',
    mcp_chatgpt: 'bg-colour-purple-01',
    mcp_gemini:  'bg-colour-purple-01',
    mcp:         'bg-colour-purple-01'
};

export const getLeadSourceBgClass = (rawSource) => {
    const channel = normalizeLeadChannel(rawSource);
    if (!channel) return 'bg-colour-gray-02';
    return CHANNEL_BG[channel] || 'bg-colour-gray-02';
};

// ---------------------------------------------------------------------------
// Rich source profile — used by Home Activity feed to show a meaningful icon
// per event rather than a generic "Email" / "API" text label. The icon must
// reflect the lead's ORIGIN — the channel it actually arrived through — and
// nothing else. Resolution order:
//
//   1. CRM-origin webhook   → GHL / FUB / Salesforce  (origin channel only)
//   2. Email inbox          → Gmail / Outlook / generic envelope, chosen from
//                             the CONNECTED inbox provider only — never the
//                             sender's email address
//   3. Normalized channel   → sms / x / mls / api / manual / mcp_*
//   4. Fallback             → API
//
// The icon reflects the LEAD's origin, not a per-copy dedup-hit channel. A
// lead that came in via Claude (mcp_claude) and later received an email copy
// still shows the Claude icon on every activity row — origin is a property of
// the lead, not of whichever inbound copy a given row represents.
//
// IMPORTANT: a CRM icon (GHL / FUB / Salesforce) is shown ONLY when the lead
// genuinely originated from that CRM's webhook — i.e. source/originSource is
// the CRM key (set in ghlContactWebhook.js / fubContactWebhook.js /
// salesforceContactWebhook.js, which also stamp crmIds). Signals like
// `crmContactUrl`, `customFields.leadsource`, and `*WriteBack` are CRM
// LINKAGE artifacts — a reverse-lookup match against a connected CRM
// (enrichmentProcessor.js lookupCrmContact) or an outbound sync — NOT the
// origin, so they must never drive the source icon. (`crmContactUrl` still
// powers the "view in CRM" link elsewhere; that's a separate concern.)
//
// Icons are referenced by string key so the renderer can use either an SVG
// asset from src/assets/icons or a HugeIcon — kept out of this module to
// avoid coupling pure logic to React imports.
// ---------------------------------------------------------------------------

// Keys consumers can switch on. Order roughly matches detection specificity.
export const SOURCE_PROFILES = {
    gmail:      { key: 'gmail',      label: 'Gmail',       iconKey: 'gmail' },
    outlook:    { key: 'outlook',    label: 'Outlook',     iconKey: 'outlook' },
    email:      { key: 'email',      label: 'Email',       iconKey: 'email-generic' },
    sms:          { key: 'sms',          label: 'SMS',              iconKey: 'sms' },
    sms_personal: { key: 'sms_personal', label: 'Text (forwarded)',  iconKey: 'messages' },
    sms_twilio:   { key: 'sms_twilio',   label: 'Twilio',           iconKey: 'twilio' },
    x:          { key: 'x',          label: 'X',           iconKey: 'x' },
    mls:        { key: 'mls',        label: 'MLS',         iconKey: 'mls' },
    ghl:        { key: 'ghl',        label: 'GoHighLevel', iconKey: 'ghl' },
    fub:        { key: 'fub',        label: 'Follow Up Boss', iconKey: 'fub' },
    salesforce: { key: 'salesforce', label: 'Salesforce',  iconKey: 'salesforce' },
    api:         { key: 'api',         label: 'API',       iconKey: 'api' },
    manual:      { key: 'manual',      label: 'Manual',    iconKey: 'manual' },
    mcp_claude:  { key: 'mcp_claude',  label: 'Claude',    iconKey: 'claude' },
    mcp_chatgpt: { key: 'mcp_chatgpt', label: 'ChatGPT',   iconKey: 'chatgpt' },
    mcp_gemini:  { key: 'mcp_gemini',  label: 'Gemini',    iconKey: 'gemini' },
    mcp:         { key: 'mcp',         label: 'AI Agent',  iconKey: 'ai-agent' }
};

// Maps an ORIGIN source value (data.originSource) to a CRM
// profile key, but ONLY when that value is the CRM-origin marker the
// webhook ingest paths stamp ('ghl' / 'fub' / 'salesforce' — see
// ghlContactWebhook.js etc.). Deliberately strict: it does NOT match free-text
// CRM names that might appear in linkage fields like customFields.leadsource,
// because those describe where a lead is *linked*, not where it came *from*.
const crmFromOrigin = (rawSource) => {
    if (!rawSource || typeof rawSource !== 'string') return null;
    const s = rawSource.toLowerCase().trim();
    if (s === 'ghl' || s === 'gohighlevel') return 'ghl';
    if (s === 'fub' || s === 'followupboss' || s === 'follow up boss') return 'fub';
    if (s === 'salesforce') return 'salesforce';
    return null;
};

/**
 * Resolve the best source profile for a feed event or a lead.
 *
 * The result reflects the LEAD's origin, resolved purely from the lead doc.
 * Callers may pass an `event` (from deriveLeadEvents) instead of a `lead` —
 * we only read its `event.lead.data`, never the per-hit `event.source` /
 * `event.sender`, so the same lead resolves to the same icon on every
 * activity row regardless of which inbound copy that row represents.
 *
 * @param {Object} args
 * @param {Object} [args.lead]   The lead doc ({ data: {...} } shape).
 * @param {Object} [args.event]  An event from deriveLeadEvents (its lead is read).
 * @returns {Object} one of SOURCE_PROFILES.
 */
export const resolveLeadSourceProfile = ({ lead, event } = {}) => {
    const data = lead?.data || event?.lead?.data || {};

    // 1. CRM-origin webhook. Only a lead that literally came from a CRM
    //    webhook carries 'ghl' / 'fub' / 'salesforce' as its origin — and that
    //    identity survives ONLY in `originSource`. `data.source` is the
    //    normalized channel, which the backend collapses to 'api' for CRM
    //    origins (batchedFirestore.js normalizeChannel), so it can't be used
    //    here.
    const crmOrigin = crmFromOrigin(data.originSource);
    if (crmOrigin) return SOURCE_PROFILES[crmOrigin];

    // Origin channel for everything else. customFields.leadsource is
    // intentionally NOT consulted: it's a CRM field value, not the lead's
    // origin. The normalized channel (data.source) and the raw origin
    // (originSource) collapse identically for non-CRM channels.
    const rawSource = data.source || data.originSource || null;
    const channel = normalizeLeadChannel(rawSource);

    // 2. Email — show Gmail / Outlook ONLY when the lead was pulled from a
    //    connected inbox whose provider we resolved at ingest. That provider
    //    is the customer's connected Gmail vs Outlook account (the email
    //    integration in Integrations.js / unipile_accounts), persisted on
    //    `data.sourceMetadata.inboxProvider`. We never infer the provider from
    //    the SENDER's email address — the sender is not our inbox, and a deal
    //    forwarded from a @gmail.com sender into an Outlook inbox must not show
    //    Gmail. Unknown inbox provider → generic envelope.
    if (channel === 'email') {
        const inboxProvider = data.sourceMetadata?.inboxProvider;
        if (inboxProvider === 'gmail') return SOURCE_PROFILES.gmail;
        if (inboxProvider === 'outlook') return SOURCE_PROFILES.outlook;
        return SOURCE_PROFILES.email;
    }

    // 3. SMS — distinguish a deal forwarded from the operator's own phone
    //    (Apple Messages icon) from one texted into the company's Twilio number
    //    (Twilio icon). The path is recorded as smsWriteBack.authSource at
    //    ingest (see functions/smsContactWebhook.js): 'user-forward' vs
    //    'company-twilio'. Unknown SMS falls back to the generic bubble.
    if (channel === 'sms') {
        const authSource = data.smsWriteBack?.authSource || null;
        if (authSource === 'company-twilio') return SOURCE_PROFILES.sms_twilio;
        if (authSource === 'user-forward')   return SOURCE_PROFILES.sms_personal;
        return SOURCE_PROFILES.sms;
    }

    // 4. MCP / AI agent — channel is 'mcp_claude', 'mcp_chatgpt', 'mcp_gemini', or 'mcp'.
    if (channel && channel.startsWith('mcp')) {
        return SOURCE_PROFILES[channel] || SOURCE_PROFILES.mcp;
    }

    // 5. Channel fallback.
    if (channel && SOURCE_PROFILES[channel]) return SOURCE_PROFILES[channel];

    // 6. Last resort — unknown becomes "API" (most leads without provenance
    //    came in through the generic ingest endpoint).
    return SOURCE_PROFILES.api;
};
